/**
 * `CheckoutSource` — the **trigger axis** of a checkout resolve: *why* we got
 * here. Lives on the wire so a single resolver can dispatch over `kind`
 * without each route inventing its own shape.
 *
 * Three concrete entry points exist today:
 *   - `cart`    — buyer's multi-line cart for a single seller
 *   - `offer`   — accepted offer (one listing, one quantity)
 *   - `auction` — won auction (one listing, one quantity, fixed bid)
 *
 * Per-kind required fields are colocated on the variant. The resolver picks
 * what to load from DB based on `kind`; downstream code (route, builder, UI)
 * branches with `default: unreachable(x: never)` so adding a fourth source
 * fails the build until every switch is updated.
 *
 * @see .tondocs/payments/CHECKOUT_RESOLVE_CONSOLIDATION_SPEC.md §3.1
 * @see ./checkout-intent.ts — `CheckoutItemContext` (the *per-item* price-source axis)
 */

import { z } from "zod";
import { pinned } from "./pin";

export type CheckoutSource =
  | { kind: "cart"; userId: string; sellerId: string }
  | { kind: "offer"; offerId: string }
  | { kind: "auction"; listingId: string; userId: string };

/**
 * Exhaustive allowlist of every `CheckoutSource["kind"]`. Boundary parsers
 * iterate this constant instead of hardcoding `kind === "x" || kind === "y"`
 * chains, which silently miss new variants. The `_Assert*` pins below make
 * the array's set-equality with the union compile-enforced: adding a kind to
 * the union without updating the array — or vice versa — fails the build.
 *
 * See `.tondocs/TEST_STRATEGY.md` §3 (exhaustiveness probe doctrine) and §3.4
 * (one union, one canonical declaration). Mirrors the pattern used by
 * `CHECKOUT_ITEM_CONTEXT_KINDS` in `./checkout-intent.ts`.
 */
export const CHECKOUT_SOURCE_KINDS = ["cart", "offer", "auction"] as const;

// Compile-time exhaustiveness pin: the array must exactly cover
// CheckoutSource['kind']. Adding a kind to the union without updating
// the array — or vice versa — fails the build. Distinct from the
// schema↔interface `pinned()` wrapper below (that one pins the Zod
// schema; this pins the literal allowlist to the union).
// See .tondocs/TEST_STRATEGY.md §3.
type _AssertKindsCoverUnion =
  CheckoutSource["kind"] extends (typeof CHECKOUT_SOURCE_KINDS)[number]
    ? true
    : never;
type _AssertNoExtraKinds =
  (typeof CHECKOUT_SOURCE_KINDS)[number] extends CheckoutSource["kind"]
    ? true
    : never;
// Reference both so unused-locals doesn't strip them:
const _kindPinCoverUnion: _AssertKindsCoverUnion = true;
const _kindPinNoExtras: _AssertNoExtraKinds = true;
void _kindPinCoverUnion;
void _kindPinNoExtras;

export const CheckoutSourceSchema = pinned<CheckoutSource>()(
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("cart"),
      userId: z.string().min(1),
      sellerId: z.string().min(1),
    }),
    z.object({
      kind: z.literal("offer"),
      offerId: z.string().min(1),
    }),
    z.object({
      kind: z.literal("auction"),
      listingId: z.string().min(1),
      userId: z.string().min(1),
    }),
  ]),
);
